import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MobileLayout from '../../components/mobile/MobileLayout';
import { useWebsiteImages, slotBackground, slotUrl, slotList, quote } from '../../lib/publicContent';
import { ed, edAbout, useAboutUs, useEditing, usePageMeta } from '../../lib/editTexts';
import { CREST_SYMBOLS } from '../../lib/editKeys';

// Ueber uns = zentrale Wahrheit mit der App (about_us); gleiche Kennungen wie die Desktop-Seite (About.jsx).
const CREST_IMAGES = ['KL0', 'KL1', 'KL2', 'KL3', 'KL4'];

const teaser = (paragraphs, length) => {
  const first = paragraphs[0] || '';
  return first.length > length ? `${first.slice(0, length)}…` : first;
};

const Leader = ({ photo, slot, slotIndex, kicker, name, teaserText, teaserPath, children }) => {
  const { t } = useTranslation();
  const editing = useEditing();
  const [open, setOpen] = useState(false);
  const shown = open || editing;   // Bearbeitungsmodus: aufgeklappt, damit jeder Text anklickbar ist
  const labelKey = shown ? 'about.show_less' : 'about.show_more';
  return (
    <>
      <div className="m-photo" data-slot={slot} data-slot-index={slotIndex} style={{ height: 240, backgroundImage: `url("${photo}")`, backgroundPosition: 'center top', borderRadius: 4 }} />
      <div style={{ padding: '16px 0 26px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#C0291F', marginBottom: 8 }}>{kicker}</div>
        <h3 style={{ margin: '0 0 10px', fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 22, lineHeight: 1.12 }}>{name}</h3>
        {!shown && <p {...edAbout(teaserPath)} style={{ margin: '0 0 14px', fontSize: 14, lineHeight: 1.6, color: '#5e554b' }}>{teaserText}</p>}
        {shown && <div style={{ margin: '0 0 14px', fontSize: 14, lineHeight: 1.7, color: '#5e554b', display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="m-link"
          style={{ background: 'none', border: 'none', borderBottom: '2px solid #C0291F', padding: '0 0 3px', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          <span {...ed(labelKey)}>{t(labelKey)}</span>{shown ? ' ↑' : ' ↓'}
        </button>
      </div>
    </>
  );
};

export default function MobileAbout() {
  const { t, i18n } = useTranslation();
  const about = useAboutUs();
  const slots = useWebsiteImages();
  const editing = useEditing();
  usePageMeta('about');
  const [vision, setVision] = useState(0);

  const visions = about.list('vision_cards');
  const values = about.list('values');
  const logoPoints = about.list('logo_points');
  const activeVision = visions[Math.min(vision, Math.max(visions.length - 1, 0))];
  const rami = about.link('leadership.global.pastor.cta');
  const pastorBio = about.paras('leadership.global.pastor.bio');
  const ladyBio = about.paras('leadership.global.first_lady.bio');
  const localBio = about.paras('leadership.local_leadership.pastor_bio');
  const nameStyle = { fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 16, color: '#26211C' };
  const crestLogo = slotList(slots, 'brand.logo')[0];

  return (
    <MobileLayout>
      <section className="m-hero">
        <span className="m-kicker" {...edAbout('hero.eyebrow')}>{about.text('hero.eyebrow')}</span>
        <h1 {...edAbout('hero.title')}>{about.text('hero.title')}</h1>
        <div {...edAbout('hero.paragraphs')}>
          {about.paras('hero.paragraphs').map((text, i) => <p key={i}>{text}</p>)}
        </div>
      </section>

      <div className="m-photo" data-slot="page.about" style={{ height: 220, ...slotBackground(slots, 'page.about', 0, '/kac-assets/photos/DSC00096.jpg') }} />

      {/* LEITUNG */}
      <section className="m-sec">
        <div className="m-head">
          <span className="m-kicker" {...edAbout('leadership_story.title')}>{about.text('leadership_story.title')}</span>
          <h2 {...ed('about.leadership_heading')}>{t('about.leadership_heading')}</h2>
        </div>

        <Leader
          photo={slotUrl(slots, 'leadership.team', 0, '/kac-assets/leadership/PastorRandolphWife.jpg')}
          slot="leadership.team"
          slotIndex={0}
          kicker={<span {...edAbout('leadership.global.title')}>{about.text('leadership.global.title')}</span>}
          name={<span {...edAbout('leadership.global.subtitle')}>{about.text('leadership.global.subtitle')}</span>}
          teaserText={teaser(pastorBio, 150)}
          teaserPath="leadership.global.pastor.bio"
        >
          <div {...edAbout('leadership.global.pastor.name')} style={nameStyle}>{about.text('leadership.global.pastor.name')}</div>
          <div {...edAbout('leadership.global.pastor.bio')} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pastorBio.map((text, i) => <p key={i} style={{ margin: 0 }}>{text}</p>)}
          </div>
          <div {...edAbout('leadership.global.first_lady.name')} style={nameStyle}>{about.text('leadership.global.first_lady.name')}</div>
          <div {...edAbout('leadership.global.first_lady.bio')} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {ladyBio.map((text, i) => <p key={i} style={{ margin: 0 }}>{text}</p>)}
          </div>
          {rami.url && (
            <a href={rami.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: '#C0291F' }}>
              {rami.label} →
            </a>
          )}
        </Leader>

        <div style={{ borderTop: '1px solid rgba(120,100,70,0.28)', paddingTop: 26 }}>
          <Leader
            photo={slotUrl(slots, 'leadership.lead', 0, '/kac-assets/leadership/PastorDaniel.jpg')}
            slot="leadership.lead"
            kicker={<span {...edAbout('leadership.local_leadership.title')}>{about.text('leadership.local_leadership.title')}</span>}
            name={<span {...edAbout('leadership.local_leadership.subtitle')}>{about.text('leadership.local_leadership.subtitle')}</span>}
            teaserText={teaser(localBio, 150)}
            teaserPath="leadership.local_leadership.pastor_bio"
          >
            <div {...edAbout('leadership.local_leadership.pastor_bio')} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {localBio.map((text, i) => <p key={i} style={{ margin: 0 }}>{text}</p>)}
            </div>
          </Leader>
        </div>
      </section>

      {/* LEITVERS */}
      <section className="m-red" style={{ padding: '34px 22px', marginTop: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: 'rgba(255,255,255,0.78)', marginBottom: 14 }}>
          <span {...edAbout('key_scripture.title')}>{about.text('key_scripture.title')}</span> · <span {...edAbout('key_scripture.reference')}>{about.text('key_scripture.reference')}</span>
        </div>
        <blockquote {...edAbout('key_scripture.text')} style={{ margin: 0, fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 500, fontSize: 21, lineHeight: 1.38 }}>
          {quote(i18n.language, about.text('key_scripture.text'))}
        </blockquote>
      </section>

      {/* VISION */}
      <section className="m-sec">
        <div className="m-head" style={{ marginBottom: 4 }}>
          <span className="m-kicker" {...ed('about.vision_kicker')}>{t('about.vision_kicker')}</span>
          <h2 {...edAbout('sections.vision_title')}>{about.text('sections.vision_title')}</h2>
        </div>
        {visions.map((position, i) => {
          const open = editing || position === activeVision;
          return (
            <button
              key={position}
              type="button"
              onClick={() => setVision(i)}
              style={{
                display: 'flex', gap: 14, width: '100%', textAlign: 'left', padding: '20px 0',
                borderBottom: '1px solid rgba(120,100,70,0.28)', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                background: 'none', cursor: 'pointer', fontFamily: 'inherit', alignItems: 'flex-start',
              }}
            >
              <span className="m-num" style={{ color: open ? '#C0291F' : '#C2B6A4' }}>{String(i + 1).padStart(2, '0')}</span>
              <span style={{ flex: 1 }}>
                <h3 {...edAbout(`vision_cards.${position}.title`)} style={{ margin: '0 0 6px', fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 600, fontSize: 17, color: open ? '#26211C' : '#857A6E' }}>
                  {about.text(`vision_cards.${position}.title`)}
                </h3>
                {open && <p {...edAbout(`vision_cards.${position}.text`)} style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: '#5e554b' }}>{about.text(`vision_cards.${position}.text`)}</p>}
              </span>
            </button>
          );
        })}
      </section>

      {/* WERTE */}
      <section className="m-sec-tight">
        <div className="m-head" style={{ marginBottom: 0 }}>
          <span className="m-kicker" {...ed('about.values_kicker')}>{t('about.values_kicker')}</span>
          <h2 {...edAbout('sections.values_title')}>{about.text('sections.values_title')}</h2>
        </div>
        {values.map((position, i) => (
          <div key={position} style={{ display: 'flex', gap: 14, alignItems: 'baseline', padding: '16px 0', borderBottom: '1px solid rgba(120,100,70,0.22)' }}>
            <span style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 14, color: '#C0291F', width: 22, flexShrink: 0 }}>{String(i + 1).padStart(2, '0')}</span>
            <div>
              <h3 {...edAbout(`values.${position}.title`)} style={{ margin: '0 0 2px', fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 600, fontSize: 17 }}>{about.text(`values.${position}.title`)}</h3>
              <p {...edAbout(`values.${position}.description`)} style={{ margin: 0, fontSize: 13, color: '#857A6E' }}>{about.text(`values.${position}.description`)}</p>
            </div>
          </div>
        ))}
      </section>

      {/* WAPPEN */}
      <section className="m-sec-tight">
        <div className="m-head" style={{ marginBottom: 24 }}>
          <span className="m-kicker" {...edAbout('sections.logo_title')}>{about.text('sections.logo_title')}</span>
          <h2 {...ed('about.crest_heading')}>{t('about.crest_heading')}</h2>
        </div>
        <img data-slot="brand.logo" src={crestLogo ? crestLogo.url : '/kac-assets/KAC-Badge.png'} alt={(crestLogo && crestLogo.alt) || t('about.crest_alt')} style={{ width: 190, display: 'block', margin: '0 auto 26px' }} />
        {CREST_SYMBOLS.map((symbol, i) => {
          const position = logoPoints[i] !== undefined ? logoPoints[i] : i;
          return (
            <div key={symbol} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '13px 0', borderBottom: '1px solid rgba(120,100,70,0.22)' }}>
              <img src={`/kac-assets/logo/${CREST_IMAGES[i]}.png`} alt="" style={{ width: 38, height: 38, objectFit: 'contain', flexShrink: 0 }} />
              <div>
                <h3 {...edAbout(`logo_points.${position}.text`)} style={{ margin: '0 0 2px', fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 600, fontSize: 16 }}>{about.text(`logo_points.${position}.text`)}</h3>
                <p {...ed(`about.crest.${symbol}`)} style={{ margin: 0, fontSize: 13, color: '#857A6E' }}>{t(`about.crest.${symbol}`)}</p>
              </div>
            </div>
          );
        })}
      </section>
    </MobileLayout>
  );
}
